import { useState } from 'react';
import type { FormEvent, KeyboardEvent } from 'react';
import { faArrowRight, faStop } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Spinner, TextArea } from '@e2n/ui/v2';
import { useTranslation } from 'react-i18next';
import { useChatStore } from '../hooks/useChatStore';
import { useSendQuestion } from '../hooks/useSendQuestion';

export function ChatInput() {
  const { t } = useTranslation();
  const [question, setQuestion] = useState('');
  const isStreaming = useChatStore((state) => state.isStreaming);
  const sendQuestion = useSendQuestion();
  const canSend = question.trim().length > 0 && !isStreaming;

  const submit = (event?: FormEvent) => {
    event?.preventDefault();
    if (!canSend) {
      return;
    }
    void sendQuestion(question.trim());
    setQuestion('');
  };

  // Enter sendet, Shift+Enter macht einen Zeilenumbruch
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      event.key === 'Enter' &&
      !event.shiftKey &&
      !event.nativeEvent.isComposing
    ) {
      submit(event);
    }
  };

  return (
    <form onSubmit={submit} className="relative">
      <TextArea
        rows={4}
        value={question}
        onChange={(event) => setQuestion(event.target.value)}
        onKeyDown={onKeyDown}
        placeholder={t('domain.ai.chat.input.placeholder')}
        aria-label={t('domain.ai.chat.input.placeholder')}
        style={{ resize: 'none' }}
      />
      <div className="absolute right-2 bottom-2">
        {isStreaming ? (
          <StopButton />
        ) : (
          <Button
            type="submit"
            size="small"
            iconOnly
            disabled={!canSend}
            aria-label={t('domain.ai.chat.input.send')}>
            <FontAwesomeIcon icon={faArrowRight} className="size-4" />
          </Button>
        )}
      </div>
    </form>
  );
}

function StopButton() {
  const { t } = useTranslation();
  const cancel = useChatStore((state) => state.cancel);

  return (
    <Button
      type="button"
      variant="ghost"
      size="small"
      iconOnly
      onClick={cancel}
      aria-label={t('domain.ai.chat.input.stop')}
      className="relative">
      <Spinner size="md" variant="dark-grey" aria-hidden className="absolute" />
      <FontAwesomeIcon icon={faStop} className="size-3.5" />
    </Button>
  );
}
